import he from 'he';
import fp from 'lodash/fp';
import PropTypes from 'prop-types';
import React from 'react';
import { Button, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { branch, compose, renderComponent, setPropTypes, withHandlers, withProps, withStateHandlers } from 'recompose';
import { industryType } from '../../../constants';
import { ControlGroup, ControlOption, Icon } from '../../components';
import Loading from '../../pages/loading';

const CpdCategorySelection = ({
  industryCpdCategorys,
  childrenCpdCategorys,
  otherCpdCategorys,
  mandatoryCpdCategorys,
  validationMessages,
  onSelectCpdCategory,
  onSelectChildrenCpdCategory,
  cpdCategorysIds,
  planLocked,
  userIndustryType,
  setOverlayRef,
  tooltip
}) => (
    [
      mandatoryCpdCategorys.length > 0 ?
        <Row key="mandatory-categories" className='no-gutter'>
          <h3>
            Mandatory Categories
            <OverlayTrigger ref={(ref) => setOverlayRef(ref)} placement='bottom' overlay={tooltip}>
              <Button bsStyle='plain'>
                <Icon icon='help' className='hide-on-ie' /> <span>What's this?</span>
              </Button>
            </OverlayTrigger>
          </h3>
          <ControlGroup
            id='areas-of-advice'
            control='checkbox'
            type='styled'
            label={null}
            validation={validationMessages.selectedCpdCategorys}
            validationProps={{ className: 'text-center' }}
            colProps={{ xs: 12, sm: 6, md: 3 }}>
            {
              fp.flow(
                fp.sortBy(({ name }) => fp.lowerCase(name)),
                fp.map(({ id, name }) =>
                  <ControlOption
                    key={name}
                    value={id}
                    disabled={planLocked}
                    checked={true}
                    onChange={(e) => { }}
                    label={<span>{he.decode(name)}</span>} />
                )
              )(mandatoryCpdCategorys)
            }
          </ControlGroup>
        </Row> : null,
      fp.map(
        ({ parent, children }) => (
          <Row key="children-categories" className='no-gutter'>
            <h3>{`${parent.name} - Areas of Advice`}</h3>
            <p className="text-left">{`These categories will make up you ${parent.name} CPD training content. At least one must be selected. You can adjust these at any time.`}</p>
            <ControlGroup
              id='areas-of-advice'
              control='checkbox'
              type='styled'
              label={null}
              validation={validationMessages.selectedCpdCategorys}
              validationProps={{ className: 'text-center' }}
              colProps={{ xs: 12, sm: 6, md: 3 }}>
              {
                fp.flow(
                  fp.sortBy(({ name }) => fp.lowerCase(name)),
                  fp.map(({ id, name }) =>
                    <ControlOption
                      key={name}
                      value={id}
                      disabled={planLocked}
                      checked={fp.contains(id, cpdCategorysIds)}
                      onChange={(e) => onSelectChildrenCpdCategory(children, id)}
                      label={<span>{he.decode(name)}</span>} />
                  )
                )(children)
              }
            </ControlGroup>
          </Row>
        )
      )(childrenCpdCategorys),
      otherCpdCategorys.length > 0 ?
        <Row key="other-categories" className='no-gutter'>
          {
            userIndustryType === industryType.Minimum ? <div>
              <h3>Responsible Manager</h3>
              <p className="text-left">Only select this category if you are a qualified RG105 Responsible Manager</p>
            </div> : null
          }
          <ControlGroup
            id='areas-of-advice'
            control='checkbox'
            type='styled'
            label={null}
            validation={validationMessages.selectedCpdCategorys}
            validationProps={{ className: 'text-center' }}
            colProps={{ xs: 12, sm: 6, md: 3 }}>
            {
              fp.flow(
                fp.sortBy(({ name }) => fp.lowerCase(name)),
                fp.map(({ id, name }) =>
                  <ControlOption
                    key={name}
                    value={id}
                    disabled={planLocked}
                    checked={fp.contains(id, cpdCategorysIds)}
                    onChange={(e) => onSelectCpdCategory(id)}
                    label={<span>{he.decode(name)}</span>} />
                )
              )(otherCpdCategorys)
            }
          </ControlGroup>
        </Row> : null
    ]
  );

export default compose(
  setPropTypes({
    isLoading: PropTypes.bool,
    validationMessages: PropTypes.object,
    industryCpdCategorys: PropTypes.array,
    onSelectCpdCategory: PropTypes.func,
    cpdCategorysIds: PropTypes.array,
    planLocked: PropTypes.bool
  }),
  branch(
    ({ isLoading }) => isLoading,
    renderComponent(Loading)
  ),
  withProps(
    ({
      industryCpdCategorys
    }) => {
      const mandatoryCpdCategorys = fp.filter(
        ({ mandatory }) => mandatory
      )(industryCpdCategorys)

      const childrenCpdCategorys = fp.map(
        (aoa) => ({
          parent: aoa,
          children: fp.filter(
            ({ parentCpdCategoryId }) => parentCpdCategoryId === aoa.id
          )(industryCpdCategorys)
        })
      )(mandatoryCpdCategorys)

      const otherCpdCategorys = fp.filter(
        ({ parentCpdCategoryId, mandatory }) => !mandatory && !parentCpdCategoryId
      )(industryCpdCategorys)

      return {
        mandatoryCpdCategorys: fp.flow(
          fp.filter(
            ({ children }) => children.length === 0
          ),
          fp.map(
            ({ parent }) => parent
          )
        )(childrenCpdCategorys),
        childrenCpdCategorys: fp.flow(
          fp.filter(
            ({ children }) => children.length > 0
          )
        )(childrenCpdCategorys),
        otherCpdCategorys
      }
    }
  ),
  withStateHandlers({
    overlayRef: null
  }, {
      setOverlayRef: () => (ref) => ({ overlayRef: ref })
    }),
  withProps(({ otherCpdCategorys, overlayRef }) => ({
    tooltip: (
      <Tooltip id='afsl-tooltip'>
        <Button onClick={() => overlayRef.hide()} className='pull-right full-width close-overlay' bsStyle='plain'>
          <Icon icon='close' />
        </Button>
        <p>
          This is the legislated CPD you are required to complete as outlined by FASEA.
        </p>
      </Tooltip>
    )
  })),
  withHandlers({
    onSelectChildrenCpdCategory: ({
      onSelectCpdCategory,
      cpdCategorysIds
    }) => (children, id) => {
      const selectedChildren = fp.filter(({ id }) => fp.includes(id)(cpdCategorysIds))(children)
      if (selectedChildren.length !== 1 || fp.get('id')(fp.head(selectedChildren)) !== id) {
        onSelectCpdCategory(id)
      }
    }
  })
)(CpdCategorySelection);



// WEBPACK FOOTER //
// ./src/containers/partials/cpd-settings/areas-of-advice-selection.js